import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { TextareaInputField } from "@/components/shared";
import { StepComponentProps } from "./types";

/**
 * ReasonStepComponent component allows users to enter a shared reason and optional individual reasons.
 *
 * @param form - The React Hook Form instance
 * @param selectedOrganizations - Currently selected organizations
 */
export function ReasonStepComponent({
  form,
  selectedOrganizations,
}: StepComponentProps) {
  const { t } = useTranslation(["translation", "common", "modals"]);
  const { watch } = form;
  const organizations = watch("organizations");
  const isSingleOrganization = selectedOrganizations.length === 1;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h3 className="text-lg font-semibold">
          {t("modals:updateparticipantsstepperform.step2.title")}
        </h3>
        <p className="text-sm text-gray-600">
          {isSingleOrganization
            ? t("modals:updateparticipantsstepperform.step2.descriptionsingle")
            : t("modals:updateparticipantsstepperform.step2.description")}
        </p>
      </div>

      {/* Shared Reason */}
      <TextareaInputField
        form={form}
        name="sharedReason"
        label={
          isSingleOrganization
            ? t("modals:updateparticipantsstepperform.step2.sharedreasonsingle")
            : t("modals:updateparticipantsstepperform.step2.sharedreason")
        }
        placeholder={
          isSingleOrganization
            ? t("modals:updateparticipantsstepperform.step2.sharedreasonplaceholdersingle")
            : t("modals:updateparticipantsstepperform.step2.sharedreasonplaceholder")
        }
        className="min-h-[100px]"
        formItemClassName="w-full"
      />

      {/* Individual Reasons - Only show when more than 1 organization is selected */}
      {selectedOrganizations.length > 1 && (
        <div className="space-y-2">
          <h4 className="text-sm text-muted-foreground">
            {t("modals:updateparticipantsstepperform.step2.individualreasons")}
          </h4>
          <div className="max-h-64 overflow-y-auto overflow-x-hidden">
            <Accordion type="single" collapsible className="w-full space-y-2">
              {selectedOrganizations.map((organization) => {
                // Find the index in the organizations array
                const orgIndex = organizations.findIndex(
                  (org) => org.organizationId === organization.operationalOrganizationId
                );

                return (
                  <AccordionItem
                    key={organization.operationalOrganizationId}
                    value={`org-${organization.operationalOrganizationId}`}
                    className="border rounded-lg px-3 border-b last:border-b"
                  >
                    <AccordionTrigger className="hover:no-underline">
                      <span className="text-sm font-medium">
                        {organization.operationalOrganizationName}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="overflow-x-hidden">
                      <TextareaInputField
                        form={form}
                        name={`organizations.${orgIndex}.reason`}
                        placeholder={t("modals:updateparticipantsstepperform.step2.individualreasonplaceholder")}
                        className="min-h-[80px] w-full"
                        isFormItem={false}
                      />
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        </div>
      )}
    </div>
  );
}
